/**
 * Колонка превью экрана черновика — docs/ui-design.md §6, docs/phase-1/05-ui.md §3.5.
 *
 * Здесь заканчивается бренд GeoBoost и начинается чужой клиент. Граница обозначена явно:
 * рамка .tgzone, eyebrow «так увидит подписчик» и собственный фон. Внутри пузыря нет ни
 * одного фирменного токена — любая примесь фирменного стиля прямо мешает единственной
 * работе панели: показать пост так, как его увидит подписчик.
 *
 * Два тумблера, и оба обязательны:
 *   режим  — «как выйдет в канал» (expected) против «только наша разметка» (canonical);
 *            разница между ними и есть то, что добавит Telegram сам (§3 контракта);
 *   тема   — светлая/тёмная ПРЕВЬЮ, не приложения: половина подписчиков читает канал в
 *            тёмной, а спойлер и цитата в ней выглядят иначе (ui-design §6.2).
 */

import { TRANSPORT_RICH_ALWAYS_DEFAULT } from '@smm/contracts/schemas';
import { type JSX, useState } from 'react';
import { IconMoon, IconSun } from '../components/icons.tsx';
import type { PreviewPanelProps } from '../routes/contracts.ts';
import { LengthMeter } from './LengthMeter.tsx';
import { LinkPreviewStrip } from './LinkPreviewStrip.tsx';
import { MessagePreview } from './MessagePreview.tsx';
import styles from './PreviewPanel.module.css';
import { TelegramDiff } from './TelegramDiff.tsx';
import {
  autoAddedEntities,
  type PreviewMode,
  type PreviewTheme,
  withDerivedTransport,
  withProposedMedia,
} from './validation.ts';

/** Формулировка — из docs/telegram.md §6 и ui-design §6.3. Это граница обещания, данного
 *  в карточке продукта, а не дисклеймер «на всякий случай». */
const LIMITS_NOTE =
  'Превью не воспроизводит то, что Telegram-клиент дорисовывает при отображении: содержимое ' +
  'карточки превью ссылки, собственный рендер эмодзи и анимацию, поведение кастомных эмодзи ' +
  'и обвязку канала (просмотры, реакции, подпись, «изменено»). Приёмка — сверка со ' +
  'скриншотом из настоящего клиента на тестовом канале.';

export function PreviewPanel({
  model,
  typed = null,
  proposed = null,
  proposedRef = null,
  richAlways,
  cardAction = null,
}: PreviewPanelProps): JSX.Element {
  const [mode, setMode] = useState<PreviewMode>('expected');
  const [theme, setTheme] = useState<PreviewTheme>('light');

  /**
   * Набранный текст рисуется как есть, а не канонизированным: человек должен видеть
   * последствия того, что он напечатал, включая таб и висящую пустую строку. Чем это
   * обернётся в канале, ему говорит колонка исходника блоком «что изменит Telegram» —
   * канонизировать здесь молча значило бы прятать ровно ту разницу.
   *
   * Текст рисуется КАК НАБРАН, но ТРАНСПОРТ пересчитывается: он выводится из (text, media), и
   * оставить его от сохранённой версии значило бы показывать карточку у поста, который её уже
   * потерял, — а ради этого плашка карточки и существует.
   */
  // Порядок веток нормативен: медиа кандидата подставляется ДО набранного текста, потому
  // что `withDerivedTransport` выводит метод из `base.media`. Обратный порядок затёр бы
  // картинку предложения, и превью показало бы транспорт, которого не будет.
  //
  // Набранная правка сохранится по политике бренда СЕЙЧАС (19.09.2026), поэтому и транспорт
  // её превью выводится по ней; неизвестна политика — по умолчанию, как у бэкенда без строки.
  const policy = richAlways ?? TRANSPORT_RICH_ALWAYS_DEFAULT;
  const base = proposed === null ? model.canonical : withProposedMedia(model.canonical, proposed);
  const canonical =
    typed === null
      ? base
      : withDerivedTransport(base, typed.text, typed.entities, { richAlways: policy });
  const auto = autoAddedEntities(canonical);
  const samples = auto.map((e) => canonical.text.slice(e.offset, e.offset + e.length));

  return (
    <section className={styles['panel']} aria-label="Превью поста">
      <div className={styles['head']}>
        <span className="eyebrow">Превью — так увидит подписчик</span>
        <div className={styles['spacer']} />
        <div className="seg">
          <button
            type="button"
            className={`${styles['segButton']} seg-o${theme === 'light' ? ' on' : ''}`}
            aria-pressed={theme === 'light'}
            onClick={() => {
              setTheme('light');
            }}
          >
            <IconSun /> светлая
          </button>
          <button
            type="button"
            className={`${styles['segButton']} seg-o${theme === 'dark' ? ' on' : ''}`}
            aria-pressed={theme === 'dark'}
            onClick={() => {
              setTheme('dark');
            }}
          >
            <IconMoon /> тёмная
          </button>
        </div>
      </div>

      <div className="seg" style={{ alignSelf: 'flex-start' }}>
        <button
          type="button"
          className={`${styles['segButton']} seg-o${mode === 'expected' ? ' on' : ''}`}
          aria-pressed={mode === 'expected'}
          onClick={() => {
            setMode('expected');
          }}
        >
          Как выйдет в канал
        </button>
        <button
          type="button"
          className={`${styles['segButton']} seg-o${mode === 'canonical' ? ' on' : ''}`}
          aria-pressed={mode === 'canonical'}
          onClick={() => {
            setMode('canonical');
          }}
        >
          Только наша разметка
        </button>
      </div>

      <div className="tgzone">
        {typed === null ? null : (
          <div className="tgnote" data-testid="preview-typed">
            Показана НЕСОХРАНЁННАЯ правка. Одобрение и тестовая отправка идут по сохранённой версии
            v{model.version_no} — сохраните правку, иначе уйдёт она.
          </div>
        )}
        {proposed === null ? null : (
          <div className="tgnote" data-testid="preview-proposed">
            Показано ПРЕДЛОЖЕНИЕ визуала: картинка ещё не принята и в подписываемое содержание не
            входит. Утвердить пост можно только после того, как вы примете или отклоните её.
          </div>
        )}
        <MessagePreview
          canonical={canonical}
          mediaRef={proposedRef ?? model.media_ref}
          documentRef={model.document_ref ?? null}
          mode={mode}
          theme={theme}
          brand={model.brand}
        />

        {/*
          Ветка «Telegram НИЧЕГО не добавит» не рисуется вовсе (правка 3.4 от 08.09.2026):
          это было сообщение о том, что ничего не произошло, и оно стояло под КАЖДЫМ постом
          без ссылок и хэштегов. Ветка, где Telegram что-то ДОБАВИТ, сохранена целиком — она
          не «оговорка», а единственное объяснение пунктира в пузыре и того, почему этих
          сущностей нет в нашем `entities`.
        */}
        {auto.length === 0 ? null : (
          <div className="tgnote">
            {mode === 'expected'
              ? `Пунктиром — сущности, которые добавит сам Telegram (${auto.length}: ${samples.join(', ')}). Отправить их явно нельзя — сервер их выбрасывает.`
              : `Показана только наша разметка. В канале Telegram добавит ещё ${auto.length}: ${samples.join(', ')} — переключите режим, чтобы увидеть.`}
          </div>
        )}

        <LinkPreviewStrip
          canonical={canonical}
          unsaved={typed !== null}
          richAlways={richAlways}
          action={cardAction}
        />

        <LengthMeter canonical={canonical} />

        {typed === null ? <TelegramDiff entries={model.canonicalization_diff} /> : null}

        {/*
          ГРАНИЦА ОБЕЩАНИЯ ОСТАЁТСЯ В ПАНЕЛИ, НО СВЁРНУТА (правка 3.4 от 08.09.2026).
          `docs/ui-design.md` §6.3 и чек-лист DoD требуют, чтобы эта формулировка стояла
          именно здесь, а не в документации: это граница обещания из карточки продукта, и
          удалять её нельзя. Но четыре строки служебного текста под каждым постом — самый
          длинный блок экрана, и читают его один раз в жизни. Компромисс: видимой остаётся
          одна строка, называющая ТЕМУ оговорки, а сама формулировка целиком висит `title`
          на готовом значке «?» (`cursor: help`, тот же, что у счётчика длины).
        */}
        <p className="tgnote" style={{ margin: 0 }}>
          Чего превью не показывает{' '}
          <span className={styles['hint']} title={LIMITS_NOTE}>
            ?
          </span>
        </p>
      </div>
    </section>
  );
}
